'use client';

import React from 'react';
import Link from 'next/link';
import { format } from 'date-fns';
import type { ICellRendererParams } from 'ag-grid-community';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Edit, Trash2, Archive, Copy } from 'lucide-react';

/**
 * Badge Cell Renderer
 * Renders a colored badge based on value and variant mapping
 */
export interface BadgeCellRendererProps extends ICellRendererParams {
  variantMap?: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'>;
  labelMap?: Record<string, string>;
}

export function BadgeCellRenderer(props: BadgeCellRendererProps) {
  const { value, variantMap = {}, labelMap = {} } = props;

  if (!value) return null;

  const variant = variantMap[value] || 'default';
  const label = labelMap[value] || String(value).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <Badge variant={variant} size="sm">
      {label}
    </Badge>
  );
}

/**
 * Status Badge Cell Renderer
 * Common status values with predefined colors
 */
export function StatusBadgeCellRenderer(props: ICellRendererParams) {
  const value = props.value as string;

  if (!value) return null;

  const statusMap: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'default'; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    pending: { variant: 'warning', label: 'Pending' },
    inactive: { variant: 'error', label: 'Inactive' },
    completed: { variant: 'success', label: 'Completed' },
    in_progress: { variant: 'info', label: 'In Progress' },
    cancelled: { variant: 'error', label: 'Cancelled' },
    draft: { variant: 'default', label: 'Draft' },
    approved: { variant: 'success', label: 'Approved' },
    rejected: { variant: 'error', label: 'Rejected' },
    review: { variant: 'warning', label: 'In Review' },
  };

  const normalizedValue = value.toLowerCase().replace(/\s+/g, '_');
  const config = statusMap[normalizedValue] || { variant: 'default' as const, label: value };

  return (
    <Badge variant={config.variant} size="sm">
      {config.label}
    </Badge>
  );
}

/**
 * Date Cell Renderer
 * Formats dates with optional time display
 */
export interface DateCellRendererProps extends ICellRendererParams {
  dateFormat?: string;
  showTime?: boolean;
  timeFormat?: string;
}

export function DateCellRenderer(props: DateCellRendererProps) {
  const { value, dateFormat = 'MMM dd, yyyy', showTime = false, timeFormat = 'HH:mm' } = props;

  if (!value) return <span className="text-text-tertiary">-</span>;

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return <span className="text-text-tertiary">Invalid date</span>;
  }

  return (
    <div className="flex flex-col">
      <span className="text-text-primary">{format(date, dateFormat)}</span>
      {showTime && (
        <span className="text-xs text-text-tertiary">{format(date, timeFormat)}</span>
      )}
    </div>
  );
}

/**
 * Link Cell Renderer
 * Renders clickable links
 */
export interface LinkCellRendererProps extends ICellRendererParams {
  href?: string | ((data: Record<string, unknown>) => string);
  onClick?: (data: Record<string, unknown>) => void;
}

export function LinkCellRenderer(props: LinkCellRendererProps) {
  const { value, data, href, onClick } = props;

  if (!value) return null;

  const linkHref = typeof href === 'function' ? href(data) : href;

  if (onClick) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick(data);
        }}
        className="font-medium text-primary-600 hover:text-primary-700 hover:underline dark:text-primary-400"
      >
        {value}
      </button>
    );
  }

  if (linkHref) {
    return (
      <Link
        href={linkHref}
        onClick={(e) => e.stopPropagation()}
        className="font-medium text-primary-600 hover:text-primary-700 hover:underline dark:text-primary-400"
      >
        {value}
      </Link>
    );
  }

  return <span className="font-medium text-primary-600">{value}</span>;
}

/**
 * Avatar Cell Renderer
 * Renders user avatar with name
 */
export interface AvatarCellRendererProps extends ICellRendererParams {
  nameField?: string;
  avatarField?: string;
  emailField?: string;
}

export function AvatarCellRenderer(props: AvatarCellRendererProps) {
  const { data, nameField = 'name', avatarField = 'avatar', emailField = 'email' } = props;

  if (!data) return null;

  const name = data[nameField];
  const avatar = data[avatarField];
  const email = data[emailField];

  if (!name) {
    return <span className="text-text-tertiary italic">Unassigned</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <Avatar name={name} src={avatar} size="sm" />
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-text-primary truncate">{name}</span>
        {email && (
          <span className="text-xs text-text-tertiary truncate">{email}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Currency Cell Renderer
 */
export interface CurrencyCellRendererProps extends ICellRendererParams {
  currency?: string;
  locale?: string;
}

export function CurrencyCellRenderer(props: CurrencyCellRendererProps) {
  const { value, currency = 'USD', locale = 'en-US' } = props;

  if (value === null || value === undefined) return <span className="text-text-tertiary">-</span>;

  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(Number(value));

  return <span className="font-medium">{formatted}</span>;
}

/**
 * Actions Cell Renderer
 * Renders action dropdown menu
 */
export interface ActionItem {
  label: string;
  icon?: React.ReactNode;
  onClick: (data: Record<string, unknown>) => void;
  variant?: 'default' | 'destructive';
  separator?: boolean;
  hidden?: (data: Record<string, unknown>) => boolean;
}

export interface ActionsCellRendererProps extends ICellRendererParams {
  actions: ActionItem[];
}

export function ActionsCellRenderer(props: ActionsCellRendererProps) {
  const { data, actions } = props;

  if (!data || !actions?.length) return null;

  const visibleActions = actions.filter(action => !action.hidden?.(data));

  if (visibleActions.length === 0) return null;

  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {visibleActions.map((action, index) => (
            <React.Fragment key={index}>
              {action.separator && index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(data);
                }}
                className={action.variant === 'destructive' ? 'text-red-600 focus:text-red-600' : ''}
              >
                {action.icon && <span className="mr-2">{action.icon}</span>}
                {action.label}
              </DropdownMenuItem>
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Create common action items helper
 */
export function createCommonActions(handlers: {
  onView?: ((data: Record<string, unknown>) => void) | undefined;
  onEdit?: ((data: Record<string, unknown>) => void) | undefined;
  onDuplicate?: ((data: Record<string, unknown>) => void) | undefined;
  onArchive?: ((data: Record<string, unknown>) => void) | undefined;
  onDelete?: ((data: Record<string, unknown>) => void) | undefined;
  readonly?: boolean | undefined;
}): ActionItem[] {
  const { onView, onEdit, onDuplicate, onArchive, onDelete, readonly = false } = handlers;
  const actions: ActionItem[] = [];

  if (onView) {
    actions.push({
      label: 'View Details',
      icon: <Eye className="h-4 w-4" />,
      onClick: onView,
    });
  }

  if (onEdit && !readonly) {
    actions.push({
      label: 'Edit',
      icon: <Edit className="h-4 w-4" />,
      onClick: onEdit,
    });
  }

  if (onDuplicate && !readonly) {
    actions.push({
      label: 'Duplicate',
      icon: <Copy className="h-4 w-4" />,
      onClick: onDuplicate,
    });
  }

  if (onArchive && !readonly) {
    actions.push({
      label: 'Archive',
      icon: <Archive className="h-4 w-4" />,
      onClick: onArchive,
      separator: true,
    });
  }

  if (onDelete && !readonly) {
    actions.push({
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: onDelete,
      variant: 'destructive',
      separator: !onArchive,
    });
  }

  return actions;
}
